import {
  useState,
  useEffect,
  useCallback,
  useContext,
  type ReactNode,
} from "react";
import {
  fetchUsers,
  callCreateUser,
  callAssignSubscription,
  callRemoveSubscription,
  callSendCsaPaymentReminder,
  callDeleteUser,
} from "../../../api/auth";
import type {
  UserListItem,
  CreateUserPayload,
  AssignSubscriptionPayload,
} from "../../../api/auth";
import { UsersContext } from "./UsersContext";
import { PickupContext } from "./PickupContext";
import { ModalContext } from "./ModalContext";
import { useAuthUser } from "../hooks/useAuthUser";

interface UsersContextProviderProps {
  children: ReactNode;
}

export const UsersContextProvider = ({
  children,
}: UsersContextProviderProps) => {
  const {
    token,
    isAdmin,
    userId: currentUserId,
    refetchDetails,
  } = useAuthUser();
  const { refreshPickups } = useContext(PickupContext);
  const { openModal, closeModal } = useContext(ModalContext);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const refetch = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError("No token in localStorage");
      return;
    }

    if (!isAdmin) {
      setLoading(false);
      setUsers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const list = await fetchUsers(token);
      setUsers(list);
    } catch (err) {
      setError(err);
      setUsers([]);
      console.warn(
        "Users fetch failed:",
        (err as { response?: { status?: number } })?.response?.status ?? err,
      );
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const createUser = useCallback(
    async (payload: CreateUserPayload) => {
      if (!token) throw new Error("No token in localStorage");

      const result = await callCreateUser(token, payload);
      await refetch();
      return result;
    },
    [token, refetch],
  );

  const assignSubscription = useCallback(
    async (userId: string, payload: AssignSubscriptionPayload) => {
      if (!token) throw new Error("No token in localStorage");

      await callAssignSubscription(token, userId, payload);
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === userId ? { ...u, hasCSASubscription: true } : u,
        ),
      );
      await refreshPickups();
      if (currentUserId && userId === currentUserId) {
        await refetchDetails();
      }
    },
    [token, refreshPickups, currentUserId, refetchDetails],
  );

  const removeSubscription = useCallback(
    async (userId: string) => {
      if (!token) throw new Error("No token in localStorage");

      await callRemoveSubscription(token, userId);
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === userId ? { ...u, hasCSASubscription: false } : u,
        ),
      );
      await refreshPickups();
      if (currentUserId && userId === currentUserId) {
        await refetchDetails();
      }
    },
    [token, refreshPickups, currentUserId, refetchDetails],
  );

  const sendCsaPaymentReminder = useCallback(
    async (userId: string) => {
      if (!token) throw new Error("No token in localStorage");
      const result = await callSendCsaPaymentReminder(token, userId);
      await refreshPickups();
      return result;
    },
    [token, refreshPickups],
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      if (!token) throw new Error("No token in localStorage");

      await callDeleteUser(token, userId);
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
      await refreshPickups();
    },
    [token, refreshPickups],
  );

  const confirmDeleteUser = useCallback(
    async (userId: string) => {
      try {
        await deleteUser(userId);
        closeModal();
        openModal({
          title: "Success!",
          body: "Account deleted successfully.",
          showCloseButton: true,
        });
      } catch {
        closeModal();
        openModal({
          title: "Error!",
          body: "Failed to delete account.",
          showCloseButton: true,
        });
      }
    },
    [deleteUser, closeModal, openModal],
  );

  const promptDeleteUser = useCallback(
    (userId: string) => {
      if (currentUserId && userId === currentUserId) {
        openModal({
          title: "Cannot delete",
          body: "You cannot delete your own account.",
          showCloseButton: true,
        });
        return;
      }

      const user = users.find((u) => u.userId === userId);
      const displayName = user
        ? `${user.firstName} ${user.surname}`.trim()
        : "this user";

      openModal({
        title: "WARNING",
        body: `Are you sure you want to delete the account for ${displayName}? This action cannot be undone.`,
        buttons: [
          { text: "No", callback: closeModal },
          {
            text: "Yes",
            loadingText: "Deleting...",
            callback: () => confirmDeleteUser(userId),
          },
        ],
      });
    },
    [users, currentUserId, openModal, closeModal, confirmDeleteUser],
  );

  return (
    <UsersContext.Provider
      value={{
        users,
        loading,
        error,
        refetch,
        createUser,
        assignSubscription,
        removeSubscription,
        sendCsaPaymentReminder,
        promptDeleteUser,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};
